import { Record } from 'immutable';
import { isEmpty, find } from 'lodash';

import * as PaymentFrequencies from 'commons-vehicle/PurchaseMethodsV25/constants/paymentFrequencies.js';
import PURCHASE_METHODS_V25 from 'commons-vehicle/PurchaseMethodsV25/constants/purchaseMethodsV25.js';
import CashPurchaseModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/CashPurchaseModelV25.js';
import BestIncentivesModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/BestIncentivesModelV25.js';
import FinanceModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/FinanceModelV25.js';

import CashPurchaseOptionsModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/CashPurchaseOptionsModelV25.js';
import FinanceOptionsModelV25 from 'commons-vehicle/PurchaseMethodsV25/models/FinanceOptionsModelV25.js';
import PaymentFrequencyService from 'commons-vehicle/PurchaseMethods/services/PaymentFrequencyService/PaymentFrequencyService.js';
import API_FETCH_STATUS from 'commons-vehicle/PurchaseMethodsV25/constants/apiFetchStatus.js';

const PurchaseMethodsModelV25Record = Record({
    addedItems: {
        retail: 0, // 14571.36
        items: [
            {
                category: '', // exteriorColour
                code: '', // UF3
                displayCode: '', // UF3
                description: '', // High idle switch
                retail: 0, // 1587.96
            },
        ],
    },
    bestIncentives: new BestIncentivesModelV25(),
    bestIncentivesDisclaimer: '',
    bestSellingPrice: 0, // 750.23
    bestSellingPriceDisclaimer: '', // On cash purchase

    // Purchase methods (basic)
    cashPurchase: new CashPurchaseModelV25(),
    finance: new FinanceModelV25(),
    lease: new FinanceModelV25(),

    // Purchase methods details (PaymentCalculator)
    cashPurchaseOption: new CashPurchaseOptionsModelV25(),
    financeOptions: [new FinanceOptionsModelV25()],

    configurationId: 0, // 15 ---> Available for new inventory
    disclaimer: '', // Cash purchase selling price is $32,477.00. Finance selling .....
    exteriorColourPhotoDisclaimer: '', // Models and pictures shown for illustration purposes only. ---> Available for new inventory
    exteriorColourPhotoStatus: 0, // 5 ---> Available for new inventory
    financePlan: '',
    showroomVehicleId: '', // 03290fb3-ea36-4f05-a845-6a5d2c11a358 ---> Available for new inventory
    vehicleCondition: '', // new
    vehicleId: '', // 9746661  Only available for used inventory

    // Selected settings
    cashDown: 0, // 1000
    kmPerYearPlan: 0,
    lien: 0, // 1500
    modelTrimCode: '', // SXEM81 ---> Available for new inventory
    odometer: 0,
    paymentFrequency: 0, // 12
    purchaseMethod: PURCHASE_METHODS_V25.CASH, // finance
    saleType: '', // retail
    taxPlan: '', // standard
    term: 0, // 60
    tradeIn: 0, // 2000
    transactionToken: '',
    paymentOptionsFetchStatus: API_FETCH_STATUS.IDLE,
    packageDescription: '',
    packageOptionsAndAccessories: [],
});

class PurchaseMethodsV25 extends PurchaseMethodsModelV25Record {
    constructor(args) {
        const newArgs = {};
        super({ ...args, ...newArgs });
    }

    getSelectedPurchaseMethodValue() {
        /**
         * VLP Used - For some old vehicles only cash purchase is available
         * In this case API returns empty object for Finance.
         * This condition check only for finance purchase method and if its empty finance object, It will return purchase method as Cash.
         * Purchase method would be consider as Cash for only those vehicles whose finance Obj is emtpy
         */
        if (this.purchaseMethod === PURCHASE_METHODS_V25.FINANCE) {
            if (isEmpty(this.finance)) {
                return PURCHASE_METHODS_V25.CASH;
            }
        }
        return this.purchaseMethod;
    }

    // Returns the price details according to the selected purchase method
    getSelectedPurchaseMethodPaymentOption() {
        const selectedPurchaseMethod = this.getSelectedPurchaseMethodValue();
        if (selectedPurchaseMethod === PURCHASE_METHODS_V25.LEASE && this.hasValidTerms(this.lease)) {
            return this.lease;
        } else if (
            // Should not be the lease if valid terms are not present for lease
            // Having paymentOptions in store also set lease to the defaut state
            // So for payment calculator need to use finance for both lease and finance
            selectedPurchaseMethod === PURCHASE_METHODS_V25.LEASE &&
            !this.hasValidTerms(this.lease) &&
            this.hasValidTerms(this.finance)
        ) {
            return this.finance;
        } else if (selectedPurchaseMethod === PURCHASE_METHODS_V25.FINANCE) {
            return this.finance;
        }
        return this.cashPurchase;
    }

    isCashPurchase() {
        return this.purchaseMethod === PURCHASE_METHODS_V25.CASH;
    }

    isFinance() {
        return this.purchaseMethod === PURCHASE_METHODS_V25.FINANCE;
    }

    isLease() {
        return this.purchaseMethod === PURCHASE_METHODS_V25.LEASE;
    }

    hasValidTerms(paymentOption) {
        return paymentOption?.term?.term > 0;
    }

    getSelectedFinanceTerm() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption) ? '' : paymentOption?.term;
    }

    getPurchaseMethodLabels(I18n) {
        const purchaseMethodLabels = {
            name: I18n.t('purchaseMethods.cashPurchase'),
            verb: I18n.t('purchaseMethods.cashPurchaseVerb'),
        };

        if (this.purchaseMethod === PURCHASE_METHODS_V25.LEASE) {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.lease');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.leaseVerb');
        } else if (this.purchaseMethod === PURCHASE_METHODS_V25.FINANCE) {
            purchaseMethodLabels.name = I18n.t('purchaseMethods.finance');
            purchaseMethodLabels.verb = I18n.t('purchaseMethods.financeVerb');
        }

        return purchaseMethodLabels;
    }

    getSelectedPurchaseMethodIncentives() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption) ? '' : paymentOption?.incentives;
    }

    getElectricRebate() {
        const incentives = this.getSelectedPurchaseMethodPaymentOption()?.incentives;
        return incentives?.electricRebateIncluded ? incentives?.governmentRebates : 0;
    }

    getDealerRebate() {
        const incentives = this.getSelectedPurchaseMethodIncentives();
        return isEmpty(incentives) ? '' : incentives?.dealerRebates;
    }

    getManufacturerRebates() {
        const incentives = this.getSelectedPurchaseMethodIncentives();
        return isEmpty(incentives) ? '' : incentives?.manufacturerRebates;
    }

    // Not provided by the backend (DATA-4959)
    // getSelectedRebatesAtStandardRate() {
    // }

    getSelectedFinanceCalculatedPayments() {
        const selectedFinanceTerm = this.getSelectedFinanceTerm();
        if (!selectedFinanceTerm) {
            return {
                calculatedPaymentBeforeTaxes: 0,
                calculatedPaymentTaxes: 0,
                calculatedPayment: 0,
                minimumPayment: 0,
                maximumPayment: 0,
            };
        }

        return {
            calculatedPaymentBeforeTaxes: selectedFinanceTerm.payment,
            calculatedPaymentTaxes: selectedFinanceTerm.paymentTaxes,
            calculatedPayment: selectedFinanceTerm.totalPayment,
            minimumPayment: 0,
            maximumPayment: selectedFinanceTerm.totalPayment,
        };
    }

    getPaymentFrequencyLabels(I18n) {
        const paymentFrequencyLabels = {
            periodically: '',
            perPeriod: '',
        };

        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        if (!paymentOption) {
            return paymentFrequencyLabels;
        }

        switch (paymentOption.paymentFrequency) {
            case PaymentFrequencies.WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.weekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perWeek');
                break;
            case PaymentFrequencies.BI_WEEKLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.biWeekly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perBiWeek');
                break;
            case PaymentFrequencies.SEMI_MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.semiMonthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perSemiMonth');
                break;
            case PaymentFrequencies.MONTHLY:
                paymentFrequencyLabels.periodically = I18n.t('paymentFrequencies.monthly');
                paymentFrequencyLabels.perPeriod = I18n.t('paymentFrequencies.perMonth');
                break;
            default:
                break;
        }

        return paymentFrequencyLabels;
    }

    getSelectedFinanceSellingPrice() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption) ? '' : paymentOption?.sellingPrice;
    }

    getSelectedFinanceCashDown() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption) ? '' : paymentOption?.cashDown;
    }

    getSelectedFinancePaymentFrequency() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption) ? '' : paymentOption?.paymentFrequency;
    }

    getTaxCodesString() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return isEmpty(paymentOption?.term) ? '' : paymentOption?.term?.taxesDetail;
    }

    getTaxesDisclaimer() {
        const paymentOption = this.getSelectedPurchaseMethodPaymentOption();
        return paymentOption?.taxesDisclaimer || '';
    }

    getMetadatas(paymentOptionsMetadata) {
        const selectedPurchaseMethod = this.getSelectedPurchaseMethodValue();
        if (isEmpty(paymentOptionsMetadata)) return undefined;

        return paymentOptionsMetadata.find(
            (metadatas) => metadatas?.isAvailable && metadatas?.purchaseMethod === selectedPurchaseMethod
        );
    }

    // I18n argument is the LocalService class to translate
    // paymentOptionsMetadata from store
    getSelectedFinanceKMPlanParameters(I18n, paymentOptionsMetadata) {
        const metadatas = this.getMetadatas(paymentOptionsMetadata);
        if (metadatas?.kmPerYearPlans && this?.kmPerYearPlan) {
            const kmPerYearPlans = metadatas.kmPerYearPlans.map((kmPlan) => ({
                value: kmPlan,
                label: I18n.t('KM_PLANS.ANNUAL_KM_ALLOWED', { annualKMAllowed: kmPlan }),
            }));

            return {
                kmPlanOptions: kmPerYearPlans,
                selectedKMPlan: find(kmPerYearPlans, { value: this.kmPerYearPlan }) || '',
            };
        }

        return {
            kmPlanOptions: '',
            selectedKMPlan: '',
        };
    }

    // I18n argument is the LocalService class to translate
    // paymentOptionsMetadata from store
    getSelectedFinanceFrequenciesParameters(I18n, paymentOptionsMetadata) {
        const metadatas = this.getMetadatas(paymentOptionsMetadata);

        if (metadatas?.paymentFrequencies) {
            return PaymentFrequencyService.getPaymentFrequencyDropDownValuesV25(I18n, metadatas.paymentFrequencies);
        }

        return [];
    }

    /*
       Payment calculator related
     */
    getCalculatorFinanceLeaseOptions(selectedMethod) {
        return this.financeOptions.find((option) => option.isAvailable && option.purchaseMethod === selectedMethod);
    }

    getCalculatorOptions() {
        const selectedPurchaseMethod = this.getSelectedPurchaseMethodValue();
        if (this.isCashPurchase()) return this.cashPurchaseOption;

        return this.getCalculatorFinanceLeaseOptions(selectedPurchaseMethod);
    }

    getCalculatorCashTransactionPrices() {
        const calculatorOptions = this.getCalculatorOptions();
        const { transaction } = calculatorOptions || {};
        if (!transaction) return {};

        const totalTransactionIndex = transaction.findIndex((t) => t.category === 'total'); // Total selling price index WITHOUT taxes, rebates..
        const toPayTransactionIndex = transaction.findIndex((t) => t.category === 'toPay'); // Total selling price index WITH taxes, rebates..

        // No 'total' transaction
        if (totalTransactionIndex < 0) {
            // If no 'total' and 'toPay' transaction is found, return the whole transaction array as 'transactionBase'
            if (toPayTransactionIndex < 0) return { transactionBase: transaction };

            // If only toPay' transaction is available, return the array as follow [start, ..., toPay] as 'transactionBase'
            return { transactionBase: transaction.slice(0, toPayTransactionIndex + 1) };
        }

        // If both 'total' and 'toPay' transaction exist, separate the array in 2 arrays as follow [..., total] and [total, ..., toPay]
        return {
            transactionBase: transaction.slice(0, totalTransactionIndex + 1),
            transactionTotal: transaction.slice(totalTransactionIndex, toPayTransactionIndex + 1),
        };
    }

    getTotalCostofCredit() {
        const term = this.getSelectedFinanceTerm();
        return term?.aprDetails?.totalCreditCost || 0;
    }

    getVdpHideTotalCostOfCredit() {
        const term = this.getSelectedFinanceTerm();
        return term?.aprDetails?.vdpHideTotalCostOfCredit || false;
    }

    getSelectedPriceSettings() {
        return {
            cashDown: this.cashDown,
            financePlan: this.financePlan,
            kmPerYearPlan: this.kmPerYearPlan,
            lien: this.lien,
            paymentFrequency: this.paymentFrequency,
            purchaseMethod: this.getSelectedPurchaseMethodValue(),
            saleType: this.saleType,
            sellingPrice: this.getSelectedFinanceSellingPrice(),
            taxPlan: this.taxPlan,
            term: this.getSelectedPurchaseMethodPaymentOption()?.term?.term || this.term,
            tradeIn: this.tradeIn,
            transactionToken: this.transactionToken,
        };
    }

    getAdditionalVehicleOptions() {
        if (!this?.addedItems?.items) return [];
        return this.addedItems.items.filter((item) => item.category === 'option');
    }
}

export default PurchaseMethodsV25;
